import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Unplug, ExternalLink, Copy, CheckCircle2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MSG = { ok: ["success", "Google Calendar collegato! Sincronizzo i tuoi richiami…"], annullato: ["error", "Collegamento annullato"], errore: ["error", "Collegamento non riuscito: verifica Client ID, Secret e URI di reindirizzamento"] };
const GC = "https://console.cloud.google.com";

const copy = async (t) => { try { await navigator.clipboard.writeText(t); } catch { window.prompt("Copia", t); } toast.success("Copiato"); };

const CopyField = ({ label, value, testid }) => (
  <div className="flex items-center gap-2 mt-1.5">
    <span className="text-xs text-slate-500 shrink-0">{label}</span>
    <code className="flex-1 min-w-0 truncate rounded bg-slate-100 px-2 py-1 text-xs">{value}</code>
    <button type="button" data-testid={testid} onClick={() => copy(value)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-sky-50 text-sky-700 shrink-0"><Copy className="h-3.5 w-3.5" /></button>
  </div>
);

const Step = ({ n, title, href, cta, children }) => (
  <li className="flex gap-3" data-testid={`gcal-step-${n}`}>
    <span className="h-6 w-6 shrink-0 rounded-full bg-[#0F172A] text-white text-xs font-semibold grid place-items-center mt-0.5">{n}</span>
    <div className="flex-1 min-w-0 text-sm">
      <p className="font-medium">{title}</p>
      <div className="text-slate-600 mt-0.5">{children}</div>
      {href && <a data-testid={`gcal-step-${n}-link`} href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-medium hover:border-sky-500 hover:text-sky-700 transition-colors"><ExternalLink className="h-3.5 w-3.5" />{cta}</a>}
    </div>
  </li>
);

const Wizard = ({ email, onSaved }) => {
  const origin = window.location.origin;
  const redirect = `${origin}/api/oauth/calendar/callback`;
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/calendar/config", { client_id: cid, client_secret: secret, public_base: origin }); toast.success("Credenziali salvate"); setSecret(""); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-5 space-y-5" data-testid="gcal-wizard">
      <p className="text-sm text-slate-700">Configurazione una tantum (circa 10 minuti, gratuita). Ogni pulsante apre la pagina giusta di Google Cloud in una nuova scheda.</p>
      <ol className="space-y-5">
        <Step n={1} title="Crea un progetto" href={`${GC}/projectcreate`} cta="Apri: nuovo progetto">Nome progetto "Kreditofacile", poi Crea. Assicurati che sia selezionato in alto.</Step>
        <Step n={2} title="Abilita Google Calendar API" href={`${GC}/apis/library/calendar-json.googleapis.com`} cta="Apri: Calendar API">Premi "Abilita".</Step>
        <Step n={3} title="Configura la schermata di consenso" href={`${GC}/apis/credentials/consent`} cta="Apri: schermata consenso">
          Tipo "Esterno", nome app "Kreditofacile", la tua email di supporto. In "Pubblico / Utenti di test" aggiungi la tua Gmail:
          <CopyField label="Utente di test" value={email} testid="gcal-copy-email" />
        </Step>
        <Step n={4} title="Crea l'ID client OAuth" href={`${GC}/apis/credentials/oauthclient`} cta="Apri: crea ID client">
          Tipo "Applicazione web". Incolla questi due valori:
          <CopyField label="Origine JavaScript" value={origin} testid="gcal-copy-origin" />
          <CopyField label="URI di reindirizzamento" value={redirect} testid="gcal-copy-redirect" />
          Premi Crea: Google ti mostra Client ID e Client Secret.
        </Step>
        <li className="flex gap-3">
          <span className="h-6 w-6 shrink-0 rounded-full bg-sky-600 text-white text-xs font-semibold grid place-items-center mt-0.5">5</span>
          <div className="flex-1 space-y-2.5">
            <p className="text-sm font-medium">Incolla qui le credenziali</p>
            <Input data-testid="gcal-client-id" value={cid} onChange={(e) => setCid(e.target.value)} placeholder="123456-abc.apps.googleusercontent.com" className="font-mono text-xs bg-white" />
            <Input data-testid="gcal-client-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="GOCSPX-…" className="font-mono text-xs bg-white" />
            <Button data-testid="gcal-save-config" onClick={save} disabled={busy || !cid || !secret} className="bg-[#0F172A] hover:bg-sky-600"><KeyRound className="h-4 w-4 mr-1.5" />Salva credenziali</Button>
            <p className="text-[11px] text-slate-400">Il Client Secret viene salvato cifrato e non viene più mostrato.</p>
          </div>
        </li>
      </ol>
    </div>
  );
};

export const CalendarSection = () => {
  const { user } = useAuth();
  const { data: st } = useApi("/calendar/status");
  const refresh = useRefresh();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [wizard, setWizard] = useState(false);
  useEffect(() => {
    const m = MSG[params.get("calendar")];
    if (m) { toast[m[0]](m[1]); params.delete("calendar"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const connect = async () => {
    if (!st?.configured) { setWizard(true); window.open(`${GC}/projectcreate`, "_blank", "noopener"); return; }
    try { const { data } = await api.post("/calendar/connect", { public_base: window.location.origin }); window.location.href = data.authorization_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const sync = async () => { setBusy(true); try { const { data } = await api.post("/calendar/sync"); toast.success(`Sincronizzati ${data.synced} eventi`); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const disconnect = async () => { await api.post("/calendar/disconnect"); toast.success("Google Calendar scollegato"); refresh(); };
  const resetConfig = async () => { await api.delete("/calendar/config"); setWizard(true); refresh(); };

  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4" data-testid="calendar-section">
      <div className="flex items-start gap-3">
        <CalendarDays className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
        <div>
          <h3 className="font-display font-semibold">Google Calendar</h3>
          <p className="text-sm text-slate-500 mt-1">Richiami, appuntamenti e rinnovi entrano da soli nel calendario della tua Gmail e si aggiornano se li sposti o annulli. Gli impegni già presenti nel tuo Google Calendar vengono esclusi dagli orari prenotabili dai clienti.</p>
        </div>
      </div>
      {st?.error && <p className="text-sm text-rose-600">{st.error}</p>}
      {st?.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span data-testid="calendar-connected" className="text-sm text-emerald-700 font-medium flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />Collegato a {st.email}</span>
          {st.last_sync_at && <span className="text-xs text-slate-400">ultima sincronizzazione {new Date(st.last_sync_at).toLocaleString("it-IT")}</span>}
          <Button data-testid="calendar-sync-btn" variant="outline" size="sm" onClick={sync} disabled={busy}><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Sincronizza ora</Button>
          <Button data-testid="calendar-disconnect-btn" variant="ghost" size="sm" onClick={disconnect} className="text-rose-600"><Unplug className="h-4 w-4 mr-1.5" />Scollega</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {st?.configured && (
            <p data-testid="calendar-configured" className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />Credenziali salvate{st.client_id ? ` (${st.client_id.slice(0, 14)}…)` : ""}. Ora premi "Collega Google Calendar" e autorizza con {user?.email}.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button data-testid="calendar-connect-btn" onClick={connect} className="bg-[#0F172A] hover:bg-sky-600">Collega Google Calendar</Button>
            {!st?.configured && !wizard && <Button data-testid="calendar-show-wizard" variant="outline" onClick={() => setWizard(true)}>Ho già le credenziali</Button>}
            {st?.configured && st?.client_id && <Button data-testid="calendar-reset-config" variant="ghost" onClick={resetConfig} className="text-slate-500">Modifica credenziali</Button>}
          </div>
        </div>
      )}
      {!st?.configured && wizard && <Wizard email={user?.email || ""} onSaved={() => { setWizard(false); refresh(); }} />}
    </section>
  );
};
